import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type * as Types from 'ably'
import { useAbly } from 'ably/react'

type ChatMessage = {
  id?: string
  senderId: string
  senderName: string
  text: string
  timestamp: number | Date
}

type ChatContextType = {
  messages: ChatMessage[]
  sendMessage: (
    msg: Omit<ChatMessage, 'timestamp'> & { timestamp?: number | Date },
  ) => Promise<void>
}

const ChatContext = createContext<ChatContextType | null>(null)

type Props = {
  channelName?: string // e.g. `CHAT:${meetingId}`
  initialHistory?: 'all' | number // 'all' = fetch all retained; number = last N (approx)
  pageSize?: number // per-page limit for Ably history (<= 1000)
  children: React.ReactNode
}

export const ChatProvider: React.FC<Props> = ({
  channelName = 'CHAT',
  initialHistory = 'all',
  pageSize = 200,
  children,
}) => {
  const client = useAbly()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const channelRef = useRef<Types.RealtimeChannel | null>(null)

  const seenKeys = useRef<Set<string>>(new Set())
  const keyOf = (m: Types.Message) => {
    const d = (m.data || {}) as any
    const uuid = d.id || d.uuid
    return uuid ? `uuid:${uuid}` : `${m.id ?? ''}|${m.timestamp ?? ''}|${m.clientId ?? ''}`
  }

  useEffect(() => {
    let mounted = true
    const ch = client.channels.get(channelName)
    channelRef.current = ch

    // reset on channel change
    setMessages([])
    seenKeys.current.clear()

    const takeLastN = <T,>(arr: T[], n: number) =>
      n >= arr.length ? arr : arr.slice(arr.length - n)

    const loadHistory = async () => {
      try {
        // Ably returns newest-first when direction='backwards'
        let page = await ch.history({
          limit: Math.min(pageSize, 1000),
          direction: 'backwards',
        })

        const items: Types.Message[] = []
        while (page) {
          items.push(...page.items)
          if (!page.hasNext()) break
          page = await page.next()
        }

        let ordered = items.reverse() // oldest -> newest

        if (initialHistory !== 'all') {
          // Keep only approximately the last N across all pages
          ordered = takeLastN(ordered, Math.max(0, initialHistory || 0))
        }

        if (!mounted) return

        const mapped = ordered
          .filter((m) => m.name === 'CHAT-MESSAGE' && m.data)
          .filter((m) => {
            const k = keyOf(m)
            if (seenKeys.current.has(k)) return false
            seenKeys.current.add(k)
            return true
          })
          .map((m) => {
            const d = m.data as any
            return {
              id: d.id || d.uuid,
              senderId: d.senderId,
              senderName: d.senderName,
              text: d.text,
              timestamp: d.timestamp ?? m.timestamp ?? Date.now(),
            } as ChatMessage
          })

        if (mapped.length) setMessages((prev) => [...prev, ...mapped])
      } catch (e) {
        // no-op
      }
    }

    const onMessage = (msg: Types.Message) => {
      if (msg.name !== 'CHAT-MESSAGE' || !msg.data) return
      const k = keyOf(msg)
      if (seenKeys.current.has(k)) return
      seenKeys.current.add(k)
      const d = msg.data as any
      setMessages((prev) => [
        ...prev,
        {
          id: d.id || d.uuid,
          senderId: d.senderId,
          senderName: d.senderName,
          text: d.text,
          timestamp: d.timestamp ?? msg.timestamp ?? Date.now(),
        },
      ])
    }

    ch.attach()
      .then(loadHistory)
      .catch(() => {})
    ch.subscribe(onMessage)

    return () => {
      mounted = false
      try {
        ch.unsubscribe(onMessage)
      } catch {}
    }
  }, [client, channelName, initialHistory, pageSize])

  const sendMessage = useMemo(() => {
    return async (msg: Omit<ChatMessage, 'timestamp'> & { timestamp?: number | Date }) => {
      const ch = channelRef.current
      if (!ch) return
      const id =
        globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`
      const payload = { ...msg, id, timestamp: msg.timestamp ?? Date.now() }
      await ch.publish('CHAT-MESSAGE', payload)
    }
  }, [])

  const value = useMemo(() => ({ messages, sendMessage }), [messages, sendMessage])

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

export const useChat = () => {
  const ctx = useContext(ChatContext)
  if (!ctx) throw new Error('useChat must be used within <ChatProvider>')
  return ctx
}
